import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AiMagicIcon } from '@hugeicons/core-free-icons';
import { ModalAction } from '@overlayqa/shared/ui';
import { apiBaseUrl } from '../../lib/apiBaseUrl';
import { copyText } from '@/lib/copyText';
import { capture } from '@/lib/analytics';
import { ToolIntegrationCard } from './ToolIntegrationCard';
import { SetupDialog } from './SetupDialog';
import { BrandTile, type BrandMark } from './integrationLogos';

/*
 * Setup text, commands and config are copied verbatim from overlayqa.com/mcp
 * (OverlayQALaunch src/pages/McpPage.tsx: MCP_CONFIG, MCP_INSTALL_COMMAND,
 * CURSOR_INSTALL_URL, EDITOR_TABS paths, the hero line and the "Connect from
 * Claude.ai" steps). Keep the two in step when either changes.
 */
const MCP_CONFIG = `{
  "mcpServers": {
    "overlayqa": {
      "command": "npx",
      "args": ["@overlayqa/mcp@latest"]
    }
  }
}`;
const MCP_INSTALL_COMMAND = 'claude mcp add --scope user overlayqa -- npx -y @overlayqa/mcp@latest';
const CURSOR_INSTALL_URL =
  'https://cursor.com/en/install-mcp?name=overlayqa&config=eyJ0eXBlIjoic3RkaW8iLCJjb21tYW5kIjoibnB4IiwiYXJncyI6WyIteSIsIkBvdmVybGF5cWEvbWNwQGxhdGVzdCJdfQ==';

type AiTool = 'claude-code' | 'cursor' | 'claude';
const editorLine = (name: string) =>
  `Scan any public URL for accessibility and design-system issues, then file, label, comment on, and track bugs without leaving ${name}.`;
export const TOOLS: Record<AiTool, { mark: BrandMark; name: string; description: string }> = {
  'claude-code': { mark: 'claudeCode', name: 'Claude Code', description: editorLine('Claude Code') },
  cursor: { mark: 'cursor', name: 'Cursor', description: editorLine('Cursor') },
  claude: { mark: 'claude', name: 'Claude', description: 'Use OverlayQA in Claude without installing anything.' },
};

/** Claude Code, Cursor and Claude cards in the /integrations grid. Claude needs the hosted connection, so it shows only when the server enables it. */
export function AiToolIntegrationCards() {
  const [hosted, setHosted] = useState(false);
  const [open, setOpen] = useState<AiTool | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    void fetch(`${apiBaseUrl()}/api/mcp/oauth/availability`, { signal: controller.signal })
      .then(async response => response.ok ? response.json() : null)
      .then(data => setHosted(data?.available === true))
      .catch(() => { if (!controller.signal.aborted) setHosted(false); });
    return () => controller.abort();
  }, []);
  const tools: AiTool[] = hosted ? ['claude-code', 'cursor', 'claude'] : ['claude-code', 'cursor'];
  return <>
    {tools.map(tool => <ToolIntegrationCard key={tool} provider={tool} mark={TOOLS[tool].mark} name={TOOLS[tool].name} description={TOOLS[tool].description}
      testId={`${tool}-card`} onSetUp={() => {
        capture('integration_link_clicked', { provider: tool, placement: 'integrations_page' });
        setOpen(tool);
      }} />)}
    {open && <SetupDialog title={TOOLS[open].name} icon={AiMagicIcon} mark={<BrandTile mark={TOOLS[open].mark} />} onClose={() => setOpen(null)}>
      {open === 'claude-code' && <ClaudeCodeSteps />}
      {open === 'cursor' && <CursorSteps />}
      {open === 'claude' && <ClaudeSteps />}
    </SetupDialog>}
  </>;
}

function ClaudeCodeSteps() {
  return <div className="space-y-5">
    <div>
      <h3 className="text-sm font-medium text-gray-900 mb-2">Design QA in one command</h3>
      <Snippet tool="claude-code" item="commands" text={MCP_INSTALL_COMMAND} copyLabel="Copy command" />
      <p className="mt-2 text-xs text-gray-500">Run this in your terminal to add OverlayQA to Claude Code for all your projects. Restart Claude Code, then ask it to list your OverlayQA projects to connect your account.</p>
      <p className="mt-2 text-xs text-gray-500">URL reviews cannot use your browser’s signed-in session. For a page behind sign-in, run Auto Review in the OverlayQA Chrome extension on that page.</p>
    </div>
    <Snippet tool="claude-code" item="config" text={MCP_CONFIG} copyLabel="Copy config" path=".mcp.json in your project root" />
    <NpxNote />
  </div>;
}

function CursorSteps() {
  return <div className="space-y-5">
    <a href={CURSOR_INSTALL_URL} target="_blank" rel="noopener noreferrer" className="btn-primary text-sm inline-flex"
      onClick={() => capture('integration_install_link_clicked', { provider: 'cursor' })}>
      <ModalAction kind="open">One-click install in Cursor</ModalAction>
    </a>
    <Snippet tool="cursor" item="config" text={MCP_CONFIG} copyLabel="Copy config" path="~/.cursor/mcp.json" />
    <NpxNote />
  </div>;
}

function ClaudeSteps() {
  return <div className="space-y-4">
    <ol className="list-decimal space-y-2 pl-5 text-sm text-gray-700">
      <li>Open <a href="https://claude.ai/directory/overlayqa" className="text-primary-600 underline" target="_blank" rel="noopener noreferrer">OverlayQA in Claude&apos;s connector directory</a>. To add it by hand instead, open Settings, then Connectors, then Add custom connector, and enter <code className="font-mono text-xs">https://api.overlayqa.com/mcp</code> as the server URL.</li>
      <li>Click Connect, sign in to OverlayQA, choose the workspace Claude may use, and click Allow access.</li>
      <li>Ask Claude to list your projects or issues. Claude asks before each tool runs, and you can disconnect any time at app.overlayqa.com/mcp/connect (Connected AI apps).</li>
    </ol>
    <Link to="/mcp/connect" className="btn-secondary text-sm inline-flex"><span className="text-cap-trim">Manage connections</span></Link>
  </div>;
}

function NpxNote() {
  return <p className="text-xs text-gray-500">Or run <code className="font-mono">npx @overlayqa/mcp@latest</code> directly. Any MCP-compatible client works the same way.</p>;
}

interface SnippetProps { tool: AiTool; item: 'commands' | 'config'; text: string; copyLabel: string; path?: string }

/** A copyable block in the dashboard's existing code style (routes/issues/VerificationCard.tsx). */
function Snippet({ tool, item, text, copyLabel, path }: SnippetProps) {
  const [result, setResult] = useState<'copied' | 'failed' | null>(null);
  useEffect(() => {
    if (!result) return;
    const timer = setTimeout(() => setResult(null), 2000);
    return () => clearTimeout(timer);
  }, [result]);
  return <div>
    <div className="flex items-center justify-between gap-2 mb-2">
      {path ? <code className="truncate font-mono text-xs text-gray-500">{path}</code> : <span />}
      <button type="button" className="btn-secondary btn-sm shrink-0" onClick={() => void copyText(text).then(ok => {
        capture('integration_setup_copied', { provider: tool, item, ok });
        setResult(ok ? 'copied' : 'failed');
      })}>
        <ModalAction kind="copy">{result === 'copied' ? 'Copied' : result === 'failed' ? 'Copy failed' : copyLabel}</ModalAction>
      </button>
    </div>
    <pre className="p-3 bg-gray-50 border border-gray-100 rounded-md text-xs text-gray-700 font-mono overflow-x-auto">{text}</pre>
  </div>;
}
